import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { mediaUrl } from "@/web/useWebApi";
import { sectionAnchor } from "@/web/sections";
import { SectionWrap, WButton, resolveLink, useSiteLink } from "../theme";

export function HeroSection({ section, site, nav }) {
  const d = section.data || {};
  const dark = site.theme?.dark_hero !== false;
  const center = d.align === "center";
  const href = resolveLink(d.cta_link, nav);
  const go = useSiteLink();
  return (
    <section id={sectionAnchor(section)} data-testid="ws-hero" className="relative overflow-hidden scroll-mt-20" style={{ background: dark ? "#0A0A0A" : "var(--w-bg)", color: dark ? "#FFFFFF" : "var(--w-text)" }}>
      {d.image_url && <img src={mediaUrl(d.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />}
      <div className="absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: "var(--w-primary)" }} />
      <div className="absolute -left-24 -bottom-40 h-80 w-80 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: "var(--w-accent)" }} />
      <div className={`relative max-w-[1200px] mx-auto px-6 lg:px-10 py-24 lg:py-36 ${center ? "text-center" : ""}`}>
        {site.brand?.tagline && <div className="text-[11px] uppercase tracking-[0.35em] font-bold mb-6 animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ color: "var(--w-accent)" }}>{site.brand.tagline}</div>}
        <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl leading-[1.05] animate-in fade-in slide-in-from-bottom-3 duration-700 ${center ? "mx-auto" : ""}`} data-testid="ws-hero-headline">{d.headline || site.brand?.name}</h1>
        {d.text && <p className={`mt-6 text-base lg:text-lg opacity-80 max-w-2xl animate-in fade-in slide-in-from-bottom-3 duration-700 delay-150 ${center ? "mx-auto" : ""}`}>{d.text}</p>}
        {d.cta_label && (
          <div className="mt-10 animate-in fade-in duration-700 delay-300">
            <WButton as="a" href={href} onClick={(e) => go(e, href)} variant="accent" size="lg" data-testid="ws-hero-cta">{d.cta_label} <ArrowRight className="h-4 w-4" /></WButton>
          </div>
        )}
      </div>
    </section>
  );
}

export function AboutSection({ section }) {
  const d = section.data || {};
  return (
    <SectionWrap section={section}>
      <div className={`grid gap-10 lg:gap-16 items-start ${d.image_url ? "lg:grid-cols-[1.2fr_1fr]" : ""}`}>
        <div>
          {d.text && <div className="text-base lg:text-lg leading-relaxed opacity-90 space-y-4" data-testid="ws-about-text">{String(d.text).split(/\n{2,}/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}</div>}
          {!!(d.highlights || []).length && (
            <ul className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="ws-about-highlights">
              {d.highlights.map((h, i) => <li key={i} className="flex items-center gap-3 p-3 text-sm font-semibold" style={{ background: "var(--w-muted)", borderLeft: "3px solid var(--w-primary)" }}><CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "var(--w-primary)" }} /> {h}</li>)}
            </ul>
          )}
        </div>
        {d.image_url && <img src={mediaUrl(d.image_url)} alt="" className="w-full aspect-[4/5] object-cover" style={{ borderRadius: "var(--w-radius)" }} />}
      </div>
    </SectionWrap>
  );
}

export function TextSection({ section }) {
  const d = section.data || {};
  return (
    <SectionWrap section={section} narrow>
      <div className="text-base lg:text-lg leading-relaxed space-y-5 opacity-90" data-testid="ws-text-body">{String(d.body || "").split(/\n{2,}/).filter(Boolean).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}</div>
    </SectionWrap>
  );
}

export function CtaSection({ section, nav }) {
  const d = section.data || {};
  const style = { dark: { background: "#0A0A0A", color: "#FFFFFF" }, primary: { background: "var(--w-primary)", color: "var(--w-on-primary)" }, light: { background: "var(--w-muted)", color: "var(--w-text)" } }[d.style || "dark"];
  const href = resolveLink(d.button_link, nav);
  const go = useSiteLink();
  return (
    <section id={sectionAnchor(section)} data-testid="ws-cta" className="px-6 lg:px-10 py-16 lg:py-20 scroll-mt-20" style={style}>
      <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row lg:items-center gap-8">
        <div className="flex-1">
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight">{d.headline || section.title}</h2>
          {d.text && <p className="mt-3 text-base lg:text-lg opacity-80 max-w-2xl">{d.text}</p>}
        </div>
        {d.button_label && <WButton as="a" href={href} onClick={(e) => go(e, href)} variant={d.style === "primary" ? "light" : "accent"} size="lg" data-testid="ws-cta-button">{d.button_label} <ArrowRight className="h-4 w-4" /></WButton>}
      </div>
    </section>
  );
}
